import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { TabMenuModule } from 'primeng/tabmenu';
import { filter, map } from 'rxjs';
import { localhostOnly } from '../../shared/helpers/localhost-only';

/** "Ustawienia OSK": one tab per settings area, each tab is a child route rendered below the tab bar. */
@Component({
  selector: 'app-osk-settings',
  standalone: true,
  imports: [RouterOutlet, TabMenuModule],
  templateUrl: './osk-settings.component.html',
  styles: [
    `
      /* The business card tab keeps its live preview on screen: it gets exactly the height left
         under the tab bar and scrolls its form inside, instead of the whole page scrolling. */
      :host(.osk-settings--fill) {
        height: 100%;
        display: flex;
        flex-direction: column;
      }

      @media (max-width: 1199px) {
        :host(.osk-settings--fill) {
          height: auto;
          display: block;
        }
      }
    `,
  ],
  host: { '[class.osk-settings--fill]': 'fillsScreen()' },
})
export class OskSettingsComponent {
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);

  // The calendar is still under construction: its tab, like its route (app-spec-fas-admin-config.ts),
  // exists only on localhost.
  public readonly tabs: MenuItem[] = [
    { label: 'Wizytówka', icon: 'pi pi-id-card', routerLink: 'business-card' },
    { label: 'Egzamin wewnętrzny', icon: 'pi pi-desktop', routerLink: 'internal-exam' },
    { label: 'Stanowiska', icon: 'pi pi-link', routerLink: 'stations' },
    ...localhostOnly<MenuItem>([{ label: 'Kalendarz i jazdy', icon: 'pi pi-calendar', routerLink: 'calendar' }]),
  ];

  /**
   * p-tabMenu is OnPush and re-checks the highlighted tab only when an input changes, so back/forward
   * or the sidebar link (which lands on the default tab) would leave the previous tab highlighted.
   */
  public readonly activeTab = toSignal(
    this._router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.findActiveTab()),
    ),
    { initialValue: this.findActiveTab() },
  );

  /** Only the business card tab is laid out as a fixed-height workspace, the others scroll as a page. */
  public readonly fillsScreen = computed(() => this.activeTab()?.routerLink === 'business-card');

  private findActiveTab(): MenuItem | undefined {
    return this.tabs.find((tab) =>
      this._router.isActive(this._router.createUrlTree([tab.routerLink], { relativeTo: this._route }), {
        paths: 'subset',
        queryParams: 'ignored',
        fragment: 'ignored',
        matrixParams: 'ignored',
      }),
    );
  }
}
